import { mkdir, readFile, rename, writeFile } from 'node:fs/promises'
import { execFile } from 'node:child_process'
import { homedir } from 'node:os'
import { dirname, join } from 'node:path'
import { promisify } from 'node:util'

export type Profile = { api: string; label?: string }
export type ProfileConfig = { schema_version: 1; profiles: Record<string, Profile> }
export type TokenSource = 'flag' | 'environment' | 'keychain' | 'missing'
export type TokenResolution = { value?: string; source: TokenSource }
export type KeychainAdapter = {
  read(profile: string): Promise<string | undefined>
  write?(profile: string, token: string): Promise<void>
  remove?(profile: string): Promise<void>
}

const execFileAsync = promisify(execFile)
const KEYCHAIN_SERVICE = 'jujing-novel-cli'
export const DEFAULT_API_ORIGIN = 'https://novel.kunworlds.com'

export function resolveApiOrigin(options: {
  explicit?: string
  profile?: string
  environment?: Record<string, string | undefined>
  allowDefault: boolean
}): string | undefined {
  const candidate = options.explicit
    ?? options.profile
    ?? options.environment?.JUJING_API
    ?? (options.allowDefault ? DEFAULT_API_ORIGIN : undefined)
  return candidate ? new URL(candidate).toString().replace(/\/$/, '') : undefined
}

const defaultConfig: ProfileConfig = { schema_version: 1, profiles: {} }

export function profileConfigPath(environment: NodeJS.ProcessEnv = process.env): string {
  if (process.platform === 'win32') return join(environment.APPDATA || join(homedir(), 'AppData', 'Roaming'), 'jujing-novel', 'config.json')
  if (process.platform === 'darwin') return join(homedir(), 'Library', 'Application Support', 'jujing-novel', 'config.json')
  return join(environment.XDG_CONFIG_HOME || join(homedir(), '.config'), 'jujing-novel', 'config.json')
}

export async function loadProfiles(configPath = profileConfigPath()): Promise<ProfileConfig> {
  try {
    const value = JSON.parse(await readFile(configPath, 'utf8')) as Partial<ProfileConfig>
    if (value.schema_version !== 1 || !value.profiles || typeof value.profiles !== 'object') throw new Error('invalid config schema')
    return { schema_version: 1, profiles: value.profiles }
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === 'ENOENT') return defaultConfig
    throw new Error(`Cannot read Novel CLI config: ${error instanceof Error ? error.message : String(error)}`)
  }
}

export async function saveProfile(profile: { name: string; api: string; label?: string }, configPath = profileConfigPath()): Promise<void> {
  const api = new URL(profile.api).toString().replace(/\/$/, '')
  const existing = await loadProfiles(configPath)
  const next: ProfileConfig = {
    schema_version: 1,
    profiles: { ...existing.profiles, [profile.name]: { api, ...(profile.label ? { label: profile.label } : {}) } },
  }
  await mkdir(dirname(configPath), { recursive: true })
  const tempPath = `${configPath}.${process.pid}.tmp`
  await writeFile(tempPath, JSON.stringify(next, null, 2) + '\n', { encoding: 'utf8', mode: 0o600 })
  await rename(tempPath, configPath)
}

export function resolveToken(options: { token?: string; environment?: Record<string, string | undefined> }): TokenResolution {
  if (options.token) return { value: options.token, source: 'flag' }
  if (options.environment?.JUJING_TOKEN) return { value: options.environment.JUJING_TOKEN, source: 'environment' }
  return { source: 'missing' }
}

export async function resolveTokenWithKeychain(options: {
  token?: string
  environment?: Record<string, string | undefined>
  profile: string
  keychain?: KeychainAdapter
}): Promise<TokenResolution> {
  const resolved = resolveToken(options)
  if (resolved.value) return resolved
  const value = await (options.keychain ?? systemKeychain).read(options.profile).catch(() => undefined)
  return value ? { value, source: 'keychain' } : { source: 'missing' }
}

class SystemKeychainAdapter implements KeychainAdapter {
  async read(profile: string): Promise<string | undefined> {
    try {
      if (process.platform === 'darwin') {
        const { stdout } = await execFileAsync('security', ['find-generic-password', '-s', KEYCHAIN_SERVICE, '-a', profile, '-w'])
        return stdout.trim() || undefined
      }
      if (process.platform === 'win32') {
        const script = '$v=New-Object Windows.Security.Credentials.PasswordVault;$c=$v.Retrieve($args[0],$args[1]);$c.RetrievePassword();[Console]::Out.Write($c.Password)'
        const { stdout } = await execFileAsync('powershell.exe', ['-NoProfile', '-NonInteractive', '-Command', script, KEYCHAIN_SERVICE, profile], { windowsHide: true })
        return stdout.trim() || undefined
      }
    } catch { /* Missing credentials and unavailable platform stores both resolve as absent. */ }
    return undefined
  }

  async write(profile: string, token: string): Promise<void> {
    if (process.platform === 'darwin') {
      await execFileAsync('security', ['add-generic-password', '-U', '-s', KEYCHAIN_SERVICE, '-a', profile, '-w', token])
      return
    }
    if (process.platform === 'win32') {
      const script = '$v=New-Object Windows.Security.Credentials.PasswordVault;try{$v.Remove($v.Retrieve($args[0],$args[1]))}catch{};$v.Add((New-Object Windows.Security.Credentials.PasswordCredential($args[0],$args[1],$args[2])))'
      await execFileAsync('powershell.exe', ['-NoProfile', '-NonInteractive', '-Command', script, KEYCHAIN_SERVICE, profile, token], { windowsHide: true })
      return
    }
    throw new Error('System keychain is supported on macOS and Windows only')
  }

  async remove(profile: string): Promise<void> {
    if (process.platform === 'darwin') {
      await execFileAsync('security', ['delete-generic-password', '-s', KEYCHAIN_SERVICE, '-a', profile])
      return
    }
    if (process.platform === 'win32') {
      const script = '$v=New-Object Windows.Security.Credentials.PasswordVault;$v.Remove($v.Retrieve($args[0],$args[1]))'
      await execFileAsync('powershell.exe', ['-NoProfile', '-NonInteractive', '-Command', script, KEYCHAIN_SERVICE, profile], { windowsHide: true })
      return
    }
    throw new Error('System keychain is supported on macOS and Windows only')
  }
}

export const systemKeychain: KeychainAdapter = new SystemKeychainAdapter()
